import { useEffect, useRef, useState } from 'react';
import {
  llamadaTerminada,
  puedeLlamar,
  type EstadoTelefonia,
  type LlamadaEnCurso,
} from '@kodica/shared';

import { Iconos } from '../componentes/iconos';
import {
  Boton,
  Campo,
  Chip,
  Encabezado,
  Entrada,
  ErrorCaja,
  Panel,
  Metrica,
} from '../componentes/ui';
import { ErrorApi, pedir } from '../lib/api';
import { useRecurso } from '../lib/consultas';

const TONO: Record<string, 'ok' | 'aviso' | 'malo' | 'neutro' | 'activo'> = {
  SOLICITADA: 'neutro',
  TIMBRANDO: 'activo',
  CONTESTADA: 'ok',
  FINALIZADA: 'neutro',
  NO_CONTESTADA: 'aviso',
  OCUPADO: 'aviso',
  FALLIDA: 'malo',
};

const LEYENDA: Record<string, string> = {
  SOLICITADA: 'Marcando…',
  TIMBRANDO: 'Timbrando el teléfono de la caseta',
  CONTESTADA: 'En llamada',
  FINALIZADA: 'Finalizada',
  NO_CONTESTADA: 'No contestaron',
  OCUPADO: 'Ocupado',
  FALLIDA: 'Falló',
};

/**
 * Prueba de banco de la telefonía.
 *
 * Esto **no** es la pantalla del guardia. Aquí se escribe un número a mano para
 * validar el hardware —HT841, X3U, línea— antes de que exista el padrón
 * telefónico. En la pantalla real el guardia oprime "llamar al residente" y
 * nunca ve ni escribe un número (docs/07); el API restringe este endpoint a
 * roles administrativos justamente por eso.
 */
/**
 * Una pieza del diagnóstico. Cada una dice qué hacer si está caída: un "no
 * registrado" sin más deja al instalador adivinando cuál de los tres aparatos
 * mirar.
 */
function Pieza({
  nombre,
  arriba,
  detalleOk,
  detalleMal,
  cargando,
}: {
  nombre: string;
  arriba: boolean;
  detalleOk: string;
  detalleMal: string;
  cargando: boolean;
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-outline-variant bg-surface-container-lowest p-5">
      <span
        className={`shrink-0 rounded-full p-2 ${
          cargando
            ? 'bg-surface-container-high text-outline'
            : arriba
              ? 'bg-success-container/40 text-success'
              : 'bg-error-container text-error'
        }`}
      >
        {arriba && !cargando ? <Iconos.correcto tam={24} /> : <Iconos.alerta tam={24} />}
      </span>
      <div className="min-w-0">
        <p className="text-label-bold uppercase tracking-wider text-on-surface-variant">{nombre}</p>
        <p className="mt-1 text-body-md text-on-surface">
          {cargando ? '—' : arriba ? detalleOk : detalleMal}
        </p>
      </div>
    </div>
  );
}

export function TelefoniaPagina() {
  const conmutador = useRecurso<EstadoTelefonia>('/telefonia/estado');

  const [numero, setNumero] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [llamada, setLlamada] = useState<LlamadaEnCurso | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marcando, setMarcando] = useState(false);
  const [segundos, setSegundos] = useState(0);

  const sondeo = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sondeo mientras la llamada vive. El estado real lo llevan los eventos de
  // ARI del lado del servidor; aquí solo se refresca la vista.
  useEffect(() => {
    if (!llamada || llamadaTerminada(llamada.estado)) {
      if (sondeo.current) clearInterval(sondeo.current);
      return;
    }

    sondeo.current = setInterval(() => {
      void pedir<LlamadaEnCurso>(`/telefonia/llamadas/${llamada.id}`)
        .then(setLlamada)
        .catch(() => {});
    }, 1000);

    return () => {
      if (sondeo.current) clearInterval(sondeo.current);
    };
  }, [llamada]);

  // Cronómetro visible. El guardia necesita ver que el sistema sigue vivo, y
  // en el flujo real el visitante espera hasta dos minutos (docs/09).
  useEffect(() => {
    if (!llamada || llamadaTerminada(llamada.estado)) return;
    const t = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [llamada]);

  async function marcar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMarcando(true);
    setSegundos(0);
    try {
      setLlamada(
        await pedir<LlamadaEnCurso>('/telefonia/prueba', {
          metodo: 'POST',
          cuerpo: { numero, ...(etiqueta ? { etiqueta } : {}) },
        }),
      );
    } catch (err) {
      // Incluye el detalle por campo, no solo el titular: "Los datos enviados
      // no son válidos" a secas no dice qué corregir.
      setError(
        err instanceof ErrorApi
          ? [err.message, ...(err.errores ?? []).map((x) => `${x.campo}: ${x.mensaje}`)].join(' · ')
          : 'No se pudo marcar',
      );
    } finally {
      setMarcando(false);
    }
  }

  async function colgar() {
    if (!llamada) return;
    try {
      setLlamada(
        await pedir<LlamadaEnCurso>(`/telefonia/llamadas/${llamada.id}/colgar`, {
          metodo: 'POST',
        }),
      );
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo colgar');
    }
  }

  const enCurso = llamada && !llamadaTerminada(llamada.estado);
  const e = conmutador.datos;
  // Marcar exige las tres piezas. Habilitar el botón con el teléfono sin
  // registrar solo produce un error críptico de Asterisk medio minuto después.
  const listo = puedeLlamar(e);

  return (
    <>
      <Encabezado
        titulo="Telefonía"
        descripcion="Prueba de banco del conmutador, el gateway y el teléfono de la caseta"
      />

      {/* Tres piezas, no un resumen. Asterisk puede estar perfecto y la
          telefonía no servir para nada porque no hay teléfono registrado. */}
      <div className="mb-stack-lg grid gap-gutter md:grid-cols-3">
        <Pieza
          nombre="Conmutador"
          arriba={e?.conmutador ?? false}
          cargando={conmutador.cargando}
          detalleOk={e?.version ? `Asterisk ${e.version}` : 'Responde'}
          detalleMal="No responde. Revisa el contenedor de Asterisk"
        />
        <Pieza
          nombre="Teléfono de caseta"
          arriba={e?.telefono ?? false}
          cargando={conmutador.cargando}
          detalleOk="Extensión 100 registrada"
          detalleMal="Sin registrar. Configura el Fanvil X3U"
        />
        <Pieza
          nombre="Línea"
          arriba={e?.linea ?? false}
          cargando={conmutador.cargando}
          detalleOk="Gateway registrado"
          detalleMal="Sin registrar. Configura el HT841"
        />
      </div>

      {e && e.configurado && !listo && (
        <div className="mb-stack-md">
          <ErrorCaja>
            Falta al menos un aparato por registrar, así que no se puede marcar todavía. Cada
            tarjeta de arriba dice cuál.
          </ErrorCaja>
        </div>
      )}

      {e && !e.configurado && (
        <ErrorCaja>
          Este servidor no tiene telefonía configurada. Falta `ARI_URL` en el entorno del API, o el
          contenedor de Asterisk no está levantado (perfil `telefonia` del compose).
        </ErrorCaja>
      )}

      <div className="grid gap-gutter lg:grid-cols-2">
        <Panel>
          <div className="border-b border-outline-variant px-6 py-5">
            <h2 className="text-headline-md text-on-surface">Marcar</h2>
            <p className="mt-1 text-body-md text-on-surface-variant">
              Primero timbra el teléfono de la caseta. Al descolgar, el conmutador marca al número y
              une las dos patas — es el mismo orden del flujo real.
            </p>
          </div>

          <form onSubmit={marcar} className="space-y-4 p-6">
            <Campo etiqueta="Número" ayuda="Solo dígitos; se limpia lo demás al enviar">
              <Entrada
                type="tel"
                required
                inputMode="tel"
                placeholder="3312345678"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                disabled={!!enCurso}
              />
            </Campo>

            <Campo
              etiqueta="Qué se lee en el teléfono"
              ayuda="Lo que verá el guardia en la pantalla del X3U. Nunca el número."
            >
              <Entrada
                placeholder="Casa 24 — Sra. Ramírez"
                value={etiqueta}
                onChange={(e) => setEtiqueta(e.target.value)}
                disabled={!!enCurso}
              />
            </Campo>

            {error && <ErrorCaja>{error}</ErrorCaja>}

            <div className="flex gap-2">
              <Boton
                type="submit"
                variante="primario"
                icono={Iconos.entrar}
                disabled={marcando || !!enCurso || !listo}
              >
                {marcando ? 'Marcando…' : 'Llamar'}
              </Boton>
              {enCurso && (
                <Boton type="button" variante="peligro" onClick={() => void colgar()}>
                  Colgar
                </Boton>
              )}
            </div>
          </form>
        </Panel>

        <Panel>
          <div className="border-b border-outline-variant px-6 py-5">
            <h2 className="text-headline-md text-on-surface">Estado de la llamada</h2>
            <p className="mt-1 text-body-md text-on-surface-variant">
              Lo reportan los eventos del conmutador, no una suposición del navegador.
            </p>
          </div>

          {!llamada ? (
            <p className="p-10 text-center text-body-md text-outline">
              Todavía no se ha marcado nada.
            </p>
          ) : (
            <dl className="divide-y divide-outline-variant">
              <div className="flex items-center justify-between px-6 py-4">
                <dt className="text-body-md text-on-surface-variant">Estado</dt>
                <dd>
                  <Chip tono={TONO[llamada.estado] ?? 'neutro'}>
                    {LEYENDA[llamada.estado] ?? llamada.estado}
                  </Chip>
                </dd>
              </div>
              <div className="flex items-center justify-between px-6 py-4">
                <dt className="text-body-md text-on-surface-variant">
                  {llamada.duracionS !== null ? 'Duración' : 'Transcurrido'}
                </dt>
                <dd className="text-headline-md tabular-nums text-on-surface">
                  {formatoReloj(llamada.duracionS ?? segundos)}
                </dd>
              </div>
              <div className="flex items-center justify-between px-6 py-4">
                <dt className="text-body-md text-on-surface-variant">Grabación</dt>
                <dd>
                  <Chip tono={llamada.hayGrabacion ? 'ok' : 'neutro'}>
                    {llamada.hayGrabacion ? 'En disco' : 'Al colgar'}
                  </Chip>
                </dd>
              </div>
              {llamada.motivoFin && (
                <div className="flex items-center justify-between gap-4 px-6 py-4">
                  <dt className="text-body-md text-on-surface-variant">Motivo</dt>
                  <dd className="text-right text-body-md text-on-surface">{llamada.motivoFin}</dd>
                </div>
              )}
            </dl>
          )}
        </Panel>
      </div>
    </>
  );
}

function formatoReloj(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
